import React, { useState } from 'react'
import classNames from 'classnames'
import { useHistory } from 'react-router-dom'
import { path } from 'ramda'
import PropTypes from 'prop-types'
import AlertSetMemberCard from './AlertSetMemberCard'
import { isPhoneVerified } from './AlertSetMemberCard.helpers'
import { useUpdateGroupMembers, useDoModal } from '../user/redux/hooks'
import { useUpdateSetting } from '../user/redux/hooks'
import { useGroupMemberInit } from './redux/useGroupMemberInit'
import { useGroupMemberSave } from './redux/useGroupMemberSave'
import { getHeatStressTier, getHeatStressTierLimits } from '../payment'
import { hasHst, getDeviceLabel } from '../../common/ambient'
import bindAllActions from '../../common/bindAllActions'
import './HeatStressGroupMembers.less'

function HeatStressGroupMembers ({ device }) {
  const { updateGroupMembers } = useUpdateGroupMembers('heatStressGroupMembers')
  const { userChecked, info } = useUpdateSetting()
  const { doModal } = useDoModal()
  const history = useHistory()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all') // 'all' | 'sms' | 'email'
  const [selectedDevices, setSelectedDevices] = useState([]) // Array of MAC addresses
  const [deviceMenuOpen, setDeviceMenuOpen] = useState(false)

  // Get HST devices
  const devices = path(['devices'], device) || []
  const hstDevices = devices.filter(hasHst)

  // Initialize group members from user info
  const { groupMembers, setGroupMembers } = useGroupMemberInit({
    userChecked,
    info,
    propertyName: 'heatStressGroupMembers',
    adminLabel: 'Admin (You)'
  })

  // Detect if both SMS and email members exist (excluding admin)
  const hasSmsMembers = groupMembers.some(m => !m.admin && m.phone)
  const hasEmailMembers = groupMembers.some(m => !m.admin && m.email)
  const showFilter = hasSmsMembers && hasEmailMembers

  // Filter members based on search term AND filter type
  const filteredMembers = React.useMemo(() => {
    let filtered = groupMembers

    // Apply communication method filter first
    if (filterType === 'sms') {
      filtered = filtered.filter(m => m.admin || m.phone) // Admin always shown + members with phone
    } else if (filterType === 'email') {
      filtered = filtered.filter(m => m.admin || m.email) // Admin always shown + members with email
    }
    // filterType === 'all' shows everyone (no filter)

    // Apply device filter
    if (selectedDevices.length > 0) {
      filtered = filtered.filter(member => {
        // Admin always shown
        if (member.admin) return true

        // Check if member has ANY of the selected devices
        const memberDevices = [
          ...(member.devicesEmail || []),
          ...(member.devicesSms || [])
        ]
        return selectedDevices.some(deviceMac => memberDevices.includes(deviceMac))
      })
    }

    // Then apply search term filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(member => {
        const name = (member.name || '').toLowerCase()
        const email = (member.email || '').toLowerCase()
        const phone = (member.phone || '').toLowerCase()

        return name.includes(search) ||
               email.includes(search) ||
               phone.includes(search)
      })
    }

    return filtered
  }, [groupMembers, searchTerm, filterType, selectedDevices])

  // Click-outside handler to close device dropdown
  React.useEffect(() => {
    if (!deviceMenuOpen) return

    const handleClickOutside = (e) => {
      if (!e.target.closest('.device-filter-dropdown')) {
        setDeviceMenuOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [deviceMenuOpen])

  // Handle member save operations
  const { handleSaveMember } = useGroupMemberSave({
    groupMembers,
    updateGroupMembers,
    userActionTypePrefix: 'heat-stress-group-member',
    showErrors: true
  })

  // Detect current subscription tier
  const currentTier = getHeatStressTier({ info })
  const tierLimits = getHeatStressTierLimits(currentTier)
  const { maxMembers, smsEnabled, displayName } = tierLimits

  // SMS members: count members with verified phones
  // Construct user object for isPhoneVerified helper
  const user = { info }

  // Count non-admin members with verified phones
  const additionalSmsMembers = groupMembers.filter(m =>
    !m.admin && m.phone && isPhoneVerified(m, user)
  )

  // Count admin separately (only if they have a verified phone)
  const adminMember = groupMembers.find(m => m.admin)
  const adminHasVerifiedPhone = adminMember && isPhoneVerified(adminMember, user)
  const totalSmsMembers = (adminHasVerifiedPhone ? 1 : 0) + additionalSmsMembers.length

  const smsSlotsLeft = maxMembers - totalSmsMembers
  const isSmsOverLimit = totalSmsMembers > maxMembers

  // Determine what can be added
  const canAddSmsMembers = smsSlotsLeft > 0 && smsEnabled && !isSmsOverLimit
  const canAddEmailMembers = true // unlimited for all tiers

  if (!userChecked || !info || !info._id) {
    return <div>Loading...</div>
  }

  return (
    <div className='alert-alert-sets'>
      <h3>Manage alert recipients</h3>

      {/* Tier and usage display */}
      <div className='tier-info'>
        <p>
          <strong>{displayName}</strong>
          {currentTier !== 'free' && (
            <> - {additionalSmsMembers.length} of {maxMembers - 1} SMS members, unlimited email members</>
          )}
          {currentTier === 'free' && (
            <> - Unlimited email members</>
          )}
        </p>
      </div>

      {/* Over-limit warning */}
      {isSmsOverLimit && (
        <div className='upgrade-prompt warning'>
          <p>
            You currently have {additionalSmsMembers.length} SMS members, which exceeds your {displayName} limit of {maxMembers - 1}. Please upgrade your subscription or remove some SMS members.
          </p>
        </div>
      )}

      {/* Free tier upgrade prompt */}
      {currentTier === 'free' && (
        <div className='upgrade-prompt'>
          <p>
            You can add unlimited email members. Upgrade to a paid Heat Stress plan to add SMS alert recipients.
          </p>
          <a
            className='btn btn-primary'
            onClick={() => {
              history.push('/subscriptions/subscribe?type=heat-stress')
            }}
          >
            View Plans
          </a>
        </div>
      )}

      {/* Invite, Search, and Filter section */}
      <div className='member-controls'>
        <a
          className='btn btn-primary'
          onClick={() => {
            doModal({
              type: 'group-member-form',
              data: {
                member: null,
                smsEnabled: canAddSmsMembers,
                showAlertSets: false,
                showDevices: true,
                devices: hstDevices,
                userActionTypePrefix: 'heat-stress-group-member',
                onSave: handleSaveMember
              }
            })
          }}
        >
          Invite User
        </a>

        <div className='search-and-filter'>
          <div className='member-search-wrap'>
            <input
              type='text'
              className='form-control member-search-input'
              placeholder='Search members...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <a
                className='glyphicon x-gray'
                onClick={() => setSearchTerm('')}
              />
            )}
          </div>

          {showFilter && (
            <div className='awn-switch three-way member-filter-switch'>
              <div className={classNames('switch-container', filterType)}>
                <span className='slider' />
                <button
                  className={classNames('switch-btn', { active: filterType === 'all' })}
                  onClick={() => setFilterType('all')}
                >
                  All
                </button>
                <button
                  className={classNames('switch-btn', { active: filterType === 'sms' })}
                  onClick={() => setFilterType('sms')}
                >
                  SMS
                </button>
                <button
                  className={classNames('switch-btn', { active: filterType === 'email' })}
                  onClick={() => setFilterType('email')}
                >
                  Email
                </button>
              </div>
            </div>
          )}

          {hstDevices.length > 0 && (
            <div className='device-filter-dropdown'>
              <button
                className='btn btn-circle btn-blue device-filter-btn'
                onClick={() => setDeviceMenuOpen(!deviceMenuOpen)}
              >
                <i className='glyphicon glyphicon-filter' />
                {selectedDevices.length > 0 && (
                  <span className='filter-count'>{selectedDevices.length}</span>
                )}
              </button>

              {deviceMenuOpen && (
                <div className='device-filter-menu'>
                  <div className='device-filter-header'>
                    <a
                      onClick={() => {
                        setSelectedDevices(
                          selectedDevices.length === hstDevices.length
                            ? []
                            : hstDevices.map(d => d.macAddress)
                        )
                      }}
                    >
                      {selectedDevices.length === hstDevices.length ? 'Select None' : 'Select All'}
                    </a>
                  </div>
                  <ul className='device-filter-list'>
                    {hstDevices.map(dev => {
                      const macAddress = dev.macAddress
                      const isChecked = selectedDevices.includes(macAddress)
                      return (
                        <li key={macAddress}>
                          <label>
                            <input
                              type='checkbox'
                              checked={isChecked}
                              onChange={() => {
                                setSelectedDevices(
                                  isChecked
                                    ? selectedDevices.filter(mac => mac !== macAddress)
                                    : [...selectedDevices, macAddress]
                                )
                              }}
                            />
                            <span>{getDeviceLabel(dev)}</span>
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* At-limit message for paid tiers */}
      {!canAddSmsMembers && currentTier !== 'free' && !isSmsOverLimit && (
        <div className='upgrade-prompt'>
          <p>
            You've reached the maximum of {maxMembers - 1} SMS members for {displayName}.
            {currentTier !== 'enterprise' && ' Upgrade to add more SMS members.'} You can still add unlimited email members.
          </p>
          {currentTier !== 'enterprise' && (
            <a
              className='btn btn-primary'
              onClick={() => {
                history.push('/subscriptions/subscribe?type=heat-stress')
              }}
            >
              Upgrade Plan
            </a>
          )}
        </div>
      )}

      <div className='group-members'>
        {filteredMembers.map((member, i) => (
          <div className={classNames('group-member-wrap', `i-${i}`)} key={member._id}>
            <AlertSetMemberCard
              member={member}
              userActionTypePrefix='heat-stress-group-member'
              showAlertSets={false}
              showDevices={true}
              devices={hstDevices}
              showUtilityMenu={!member.admin}
              onEdit={(member) => {
                doModal({
                  type: 'group-member-form',
                  data: {
                    member,
                    smsEnabled: canAddSmsMembers,
                    showAlertSets: false,
                    showDevices: true,
                    devices: hstDevices,
                    userActionTypePrefix: 'heat-stress-group-member',
                    onSave: handleSaveMember
                  }
                })
              }}
              onRemove={() => {
                if (window.confirm('Are you sure you want to remove this member?')) {
                  updateGroupMembers(groupMembers.filter(m => m._id !== member._id))
                }
              }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

HeatStressGroupMembers.displayName = 'HeatStressGroupMembers'

HeatStressGroupMembers.propTypes = {
  device: PropTypes.object.isRequired
}

export default bindAllActions(HeatStressGroupMembers)
